import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button as MuiButton,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MainContainer } from '../../../components/ContentWrapper';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { PageTitle } from '../../../components/PageTitle';
import { Button } from '../../../components/Button/Button';
import { DotLottleLoader } from '../../../components/Loader';
import { IconPlus, IconPencil, IconTrash } from '../../../utils/icons';
import config from '../../../config';
import axiosInstant from '../../../api';
import { adminProductCategories } from '../../../api/endpoints';
import { ConfirmDialog } from '../expertMatching/ConfirmDialog';
import { ProductCategoryForm } from './ProductCategoryForm';
import { IProductCategory, buildVisibleRows, parentIds } from './constants';

const ProductCategories = () => {
  const [data, setData] = useState<IProductCategory[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<IProductCategory | undefined>();
  const [toDelete, setToDelete] = useState<IProductCategory | undefined>();
  // Ids of expanded (open) nodes in the tier tree.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const baseUrl = `${config.API_URL}${adminProductCategories}`;

  const fetchData = async () => {
    try {
      const res = await axiosInstant.get(baseUrl);
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Failed to load product categories');
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    setShowLoader(true);
    fetchData();
  }, []);

  const onClose = () => {
    setSelected(undefined);
    setShowForm(false);
  };

  const submit = async (payload: Partial<IProductCategory>, attributeValueIds: number[]) => {
    setSaving(true);
    try {
      let id = selected?.id;
      if (selected) {
        await axiosInstant.patch(`${baseUrl}/${selected.id}`, payload);
      } else {
        const res = await axiosInstant.post(baseUrl, payload);
        id = res.data?.id;
      }
      // Only PUT the attribute vector when it actually changed, so name-only
      // edits don't trigger an unnecessary full recompute.
      const prevIds = (selected?.attribute_values ?? []).map((v) => v.id);
      const changed =
        prevIds.length !== attributeValueIds.length ||
        [...attributeValueIds].sort().join(',') !== [...prevIds].sort().join(',');
      if (id != null && changed) {
        await axiosInstant.put(`${baseUrl}/${id}/attribute-values`, {
          attribute_value_ids: attributeValueIds,
        });
      }
      toast.success(selected ? 'Category updated' : 'Category created');
      await fetchData();
      onClose();
    } catch (err) {
      toast.error('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await axiosInstant.delete(`${baseUrl}/${toDelete.id}`);
      toast.success('Category deleted');
      await fetchData();
    } catch (err) {
      toast.error('Failed to delete category');
    } finally {
      setSaving(false);
      setToDelete(undefined);
    }
  };

  const rows = useMemo(() => buildVisibleRows(data, expanded), [data, expanded]);
  const allParentIds = useMemo(() => parentIds(data), [data]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(allParentIds));
  const collapseAll = () => setExpanded(new Set());

  return (
    <WhiteBackground>
      <MainContainer>
        <Box className="w-full flex flex-wrap justify-between items-center gap-3 pb-8">
          <PageTitle title="Categories" />
          <Button className="!flex gap-2 !px-4" onClick={() => setShowForm(true)}>
            <IconPlus />
            <Typography>New Category</Typography>
          </Button>
        </Box>

        <Box className="relative">
          {showLoader && <DotLottleLoader />}
          {!showLoader && (
            <Paper>
              <Box className="flex justify-end gap-2 p-2">
                <MuiButton size="small" onClick={expandAll} disabled={!allParentIds.length}>
                  Expand all
                </MuiButton>
                <MuiButton size="small" onClick={collapseAll} disabled={!expanded.size}>
                  Collapse all
                </MuiButton>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell>External ID</TableCell>
                      <TableCell align="center">Order</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(({ cat, depth, hasChildren }) => (
                      <TableRow key={cat.id} hover>
                        <TableCell>
                          <Box
                            className="flex items-center"
                            sx={{ pl: `${depth * 24}px` }}
                          >
                            {hasChildren ? (
                              <IconButton
                                size="small"
                                onClick={() => toggle(cat.id)}
                                sx={{ mr: 0.5 }}
                              >
                                {expanded.has(cat.id) ? (
                                  <ExpandMoreIcon fontSize="small" />
                                ) : (
                                  <ChevronRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 34, flexShrink: 0 }} />
                            )}
                            <Typography
                              sx={{ fontWeight: hasChildren ? 600 : 400 }}
                            >
                              {cat.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{cat.slug}</TableCell>
                        <TableCell>{cat.external_id || '—'}</TableCell>
                        <TableCell align="center">{cat.order}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelected(cat);
                              setShowForm(true);
                            }}
                          >
                            <IconPencil size={18} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setToDelete(cat)}
                            sx={{ color: 'var(--text)' }}
                          >
                            {/* stroke via currentColor — SVG presentation attributes can't read var() */}
                            <IconTrash width={18} height={18} stroke="currentColor" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography className="py-6" color="var(--text-2)">
                            No product categories yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </MainContainer>

      {showForm && (
        <ProductCategoryForm
          data={selected}
          list={data}
          isLoading={saving}
          onSubmit={submit}
          onClose={onClose}
        />
      )}

      <ConfirmDialog
        open={!!toDelete}
        title="Delete category"
        message={`Delete "${toDelete?.name}"? This unlinks it from any products and attributes.`}
        loading={saving}
        onCancel={() => setToDelete(undefined)}
        onConfirm={confirmDelete}
      />
    </WhiteBackground>
  );
};

export default ProductCategories;
